import { style, styleVariants } from '@vanilla-extract/css'
import { vars, focusRing } from '../design/theme.css'
import { axisTick, birdNameRow, gloss, reading, utility } from '../design/primitives/text.css'

// ---- Report header ----
export const head = style({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: vars.space[4],
  paddingBottom: vars.space[4],
  borderBottom: `1px solid ${vars.color.ink}`,
})
export const rangeLabel = style({ display: 'block', marginTop: vars.space[1] })

// ---- Period summary strip (figures + comparison) ----
export const summary = style({
  display: 'flex',
  flexWrap: 'wrap',
  gap: vars.space[7],
  paddingBlock: vars.space[5],
  borderBottom: `1px solid ${vars.color.rule}`,
})
export const sumItem = style({ display: 'flex', flexDirection: 'column', gap: vars.space[1] })
// The line of figures under a summary label — a reading, not a smaller label.
export const sumSub = style([reading, { fontSize: vars.ms.sm, color: vars.color.inkSoft }])
export const sumFig = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs['2xl'],
  lineHeight: 1,
  color: vars.color.ink,
  fontVariantNumeric: 'tabular-nums',
})
export const delta = styleVariants({
  up: { color: vars.color.green },
  down: { color: vars.color.red },
  flat: { color: vars.color.inkSoft },
})
export const changeVal = reading

// ---- Activity chart ----
export const chart = style({ paddingBlock: vars.space[5], borderBottom: `1px solid ${vars.color.rule}` })
export const bars = style({
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: '1fr',
  alignItems: 'end',
  gap: '2px',
  height: '160px',
  marginTop: vars.space[4],
})
// A six-column report does not fit a phone, and made every page below it scroll sideways.
// The TABLE scrolls inside its own box; the page does not (§18).
export const tableWrap = style({ overflowX: 'auto' })
export const barCol = style({
  position: 'relative',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  minWidth: 0,
})
export const bar = styleVariants({
  covered: { background: vars.color.ink2, width: '100%' },
  uncovered: { background: vars.color.amber, opacity: 0.55, width: '100%' },
  unknown: { background: vars.color.paper3, width: '100%' },
})
export const gapMark = styleVariants({
  uncovered: { height: '3px', background: vars.color.amber, opacity: 0.5, width: '100%' },
  unknown: { height: '2px', background: vars.color.rule, width: '100%' },
})
export const axis = style({
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: '1fr',
  gap: '2px',
  marginTop: vars.space[2],
})
// Only every nth column carries a label, so a tick may spill into the empty columns beside it
// rather than being cut off mid-figure ("00:" for 00:00) as it was at any narrow width.
export const tick = style([axisTick, { textAlign: 'center', overflow: 'visible' }])

// ---- Species table ----
export const table = style({ width: '100%', borderCollapse: 'collapse', marginTop: vars.space[3] })
export const th = style({
  textAlign: 'left',
  padding: `${vars.space[2]} ${vars.space[2]}`,
  borderBottom: `1px solid ${vars.color.ink}`,
  whiteSpace: 'nowrap',
})
// The column heads. `thLabel` is the same voice without the button — the Share column is not
// sortable, and being a bare string it inherited the page serif and read as a bold serif word
// among five mono labels.
export const thLabel = utility
export const thBtn = style([
  thLabel,
  {
    appearance: 'none',
    background: 'transparent',
    border: 0,
    padding: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: vars.space[1],
    selectors: {
      '&[aria-sort]': { color: vars.color.ink },
      '&:hover': { color: vars.color.ink },
      '&:focus-visible': focusRing,
    },
  },
])
export const thNum = style({ textAlign: 'right' })
export const row = style({
  cursor: 'pointer',
  selectors: {
    '&:hover': { background: vars.color.paper2 },
    '&:focus-within': { background: vars.color.paper2 },
  },
})
export const td = style({
  padding: `${vars.space[2]} ${vars.space[2]}`,
  borderBottom: `1px solid ${vars.color.rule}`,
  verticalAlign: 'baseline',
})
// The DATA cells never wrap. The table already scrolls inside its own box on a phone (see
// `tableWrap`), so breaking a date across three lines bought nothing and made every row three
// lines tall: "29 / Jun / 2026" under a column head that is itself nowrap. Only the species
// name, which has words to break, still wraps.
export const tdNum = style([td, reading, { textAlign: 'right', whiteSpace: 'nowrap' }])
// Every figure in the report is set at the reading rank; the RANK of a column is carried by its
// tone, not by its size. The count was ms.md/ink while share, change, first and last were 11px
// soft, so one row held numerals at two sizes reading left to right.
export const tdMeta = style([reading, { color: vars.color.inkSoft, whiteSpace: 'nowrap' }])
// The name over its other-language gloss. Stacked rather than inline because a table cell has
// to stay narrow; the gloss itself is the shared treatment, not a bare unsized <em>.
export const nameCell = style({ display: 'flex', flexDirection: 'column' })
// The bird's name, at the row rank the roll and the Journal rail use. It was a bare <span> here,
// so it simply inherited the page and read a size smaller than the same bird's name anywhere else.
export const name = birdNameRow
export const nameGloss = gloss
export const shareBarWrap = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.space[2],
  justifyContent: 'flex-end',
})
export const shareBar = style({
  height: '4px',
  width: '48px',
  background: vars.color.paper2,
  position: 'relative',
  flex: 'none',
})
export const shareFill = style({ position: 'absolute', insetBlock: 0, left: 0, background: vars.color.ink2 })

// ---- Lifetime closing band ----
export const lifetime = style({
  display: 'flex',
  flexWrap: 'wrap',
  gap: vars.space[7],
  marginTop: vars.space[7],
  paddingTop: vars.space[5],
  borderTop: `1px solid ${vars.color.rule}`,
})
export const lifeItem = style({ display: 'flex', flexDirection: 'column', gap: vars.space[1] })
export const lifeFig = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.xl,
  lineHeight: 1,
  color: vars.color.ink2,
  fontVariantNumeric: 'tabular-nums',
})
